/**
 * Effective dates for the legal documents.
 *
 * ---------------------------------------------------------------------------
 * THE DATE IS ENFORCED, NOT REMEMBERED
 * ---------------------------------------------------------------------------
 * "Last updated" went stale once already: the Terms said May 2026 while prices
 * had changed, paid search products had launched, and live billing had shipped.
 * A date nobody bumps is worse than no date, because it actively asserts the
 * document has not changed.
 *
 * So `scripts/check-legal-dates.mjs` hashes the source of each document body in
 * `Legal.tsx` and compares it against `LEGAL_HASHES` below. If a body changed
 * and its date did not, `npm run build` FAILS. The platform runs `npm run
 * build` on every deploy, so a stale date cannot reach production.
 *
 * Deliberately NOT derived from the build date or the file mtime. That would
 * bump the date on every deploy, including deploys that changed nothing in the
 * document, which destroys the one thing the date is for: telling a reader
 * whether the terms they agreed to have actually changed.
 *
 * TO CHANGE A DOCUMENT: edit the body, then run `npm run legal:bless`. It sets
 * the date to today and records the new hash in one step.
 */

export type LegalDocId = 'terms' | 'privacy' | 'cookies' | 'confidentiality';

/**
 * Human-readable effective date per document. Month and year only: a legal
 * document's precision is the month it took effect, and a day-level date
 * invites bumping for typo fixes.
 */
export const LEGAL_UPDATED: Record<LegalDocId, string> = {
  terms: 'October 2026',
  privacy: 'October 2026',
  cookies: 'October 2026',
  confidentiality: 'September 2026',
};

/**
 * SHA-256 of each document body's source, truncated to 16 hex characters.
 * Maintained by `npm run legal:bless`; never edit by hand.
 *
 * Truncation is fine here. This detects honest edits, not adversarial ones, and
 * 64 bits is far beyond the collision risk of a handful of documents.
 */
export const LEGAL_HASHES: Record<LegalDocId, string> = {
  terms: '1378a3243bb6241e',
  privacy: '28492c8848f4b3d2',
  cookies: 'bed0e40c693b97f0',
  confidentiality: '2ed3557e2f967f90',
};

/** The function name in `Legal.tsx` that renders each document's body. */
export const LEGAL_BODY_FN: Record<LegalDocId, string> = {
  terms: 'TermsBody',
  privacy: 'PrivacyBody',
  cookies: 'CookiesPage',
  confidentiality: 'SecurityBody',
};
